import React, { FC } from 'react';

import PropTypes from 'prop-types';

import Accordion, { AccordionItem } from '@adsk/alloy-react-accordion';
import { useLocale } from '@adsk/alloy-react-locale';

import { TRIAL, dayExpression } from './helpers/consts';
import {
  accordionItem,
  accordionContainer,
  titleText,
  secondaryText,
  labelText,
  footer,
  footerSideText,
  footerDateText,
} from './helpers/styles';
import { Offering, offeringPropType } from '../../helpers/types';

interface SubscriptionItemProps {
  subscription: Offering;
  expanded?: boolean;
  onExpand(): void;
}

const SubscriptionItem: FC<SubscriptionItemProps> = ({
  subscription,
  expanded,
  onExpand,
}) => {
  const {
    offeringName,
    type,
    products,
    daysRemaining,
    endDate,
    sheetCount,
    alternativeSubscription,
  } = subscription;

  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const itemTitle = <span css={titleText}>{offeringName}</span>;

  const isTrial = type === TRIAL;
  const dayText = daysRemaining && T.translate(dayExpression(daysRemaining));

  const alternativeTrialTime = T.translate('TRIAL_EXPIRE_TIME', {
    numberOfDays: alternativeSubscription?.daysRemaining,
    noun: dayText,
  });

  const trialTime = T.translate('TRIAL_EXPIRE_TIME', {
    numberOfDays: daysRemaining,
    noun: dayText,
  });

  const subscriptionTime = endDate
    ? T.translate('SUBSCRIPTION_EXPIRE_TIME', {
        date: new Date(endDate).toLocaleDateString(),
      })
    : T.translate('SUBSCRIPTION_RENEWAL_AUTOMATICALLY');

  const sheets = sheetCount ? (
    <span css={labelText}>{`${T.translate('SHEET_COUNT')} ${sheetCount}`}</span>
  ) : (
    ''
  );

  const AlternativeSpan = () => {
    if (!alternativeSubscription) return null;
    return (
      <div css={footer} style={{ marginTop: '8px' }}>
        <span css={footerSideText}> {T.translate('TRIAL')}</span>
        <span css={footerDateText}>{alternativeTrialTime}</span>
      </div>
    );
  };

  const SubscriptionSpan = () => {
    return (
      <>
        <span css={footerSideText}> {T.translate('SUBSCRIPTION')}</span>
        <span css={footerDateText} data-testid="SubscriptionItem_TimeLabel">
          {subscriptionTime}
        </span>
      </>
    );
  };

  const BottomSpan = () => {
    if (isTrial) {
      return (
        <>
          <span css={footerSideText}> {T.translate('TRIAL')}</span>
          <span css={footerDateText}>{trialTime}</span>
        </>
      );
    }

    return <SubscriptionSpan />;
  };

  return (
    <div data-testid={subscription.offeringName}>
      <Accordion
        data-testid="SubscriptionItem__Accordion"
        initialExpanded={[1]}
        multiExpand
      >
        <AccordionItem
          data-testid="SubscriptionItem__AccordionItem"
          expanded={expanded}
          onExpand={onExpand}
          title={itemTitle}
          style={accordionItem}
          label={sheets}
        >
          {products?.length > 0 && (
            <div
              css={accordionContainer}
              data-testid="SubscriptionItem_AccordionContainer"
            >
              <span css={secondaryText}>
                {T.translate('INCLUDED_IN_SUBSCRIPTION')}
              </span>
              <ul>
                {products.map(({ productName, productKey }) => (
                  <li key={productKey} css={secondaryText}>
                    {productName}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </AccordionItem>
      </Accordion>

      <div css={footer} data-testid="subscriptionItem">
        <BottomSpan />
      </div>
      <AlternativeSpan />

      <br />
    </div>
  );
};

SubscriptionItem.displayName = 'SubscriptionItem';
SubscriptionItem.propTypes = {
  /**  Single user subscription and it's products*/
  subscription: offeringPropType.isRequired,
  expanded: PropTypes.bool,
  onExpand: PropTypes.func.isRequired,
};

SubscriptionItem.defaultProps = {
  expanded: false,
};

export default SubscriptionItem;
